import React, { useState, useRef, useEffect } from 'react';
import dayjs from 'dayjs';
import { useTheme } from '@material-ui/styles';
import _ from 'lodash';
import objectSupport from 'dayjs/plugin/objectSupport';
import Tooltip from '@material-ui/core/Tooltip';

import PropTypes from 'prop-types';

import {
    Text,
    StyledCard,
    StyledCardContent,
    Avatar,
    FilledIconButton,
    MessagesIcon,
    PhoneIcon,
    IconButton,
    MoreIcon,
    PopupMenu,
    NoticeIcon,
} from '../../index';

dayjs.extend(objectSupport);

export const PatientReadyCard = ({
    data,
    onSkip,
    onMessage,
    onCall,
    onNotification,
    disabled,
    loading,
}) => {
    const theme = useTheme();

    const moreButtonRef = useRef(null);

    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const handleClickOutside = (event) => {
        if (moreButtonRef.current && !moreButtonRef.current.contains(event.target)) {
            setAnchorEl(false);
        }
    };

    // hide picker if click somewhere else
    useEffect(() => {
        document.addEventListener('click', handleClickOutside, false);
        return () => {
            document.removeEventListener('click', handleClickOutside, false);
        };
    }, []);

    return (
        <StyledCard style={{ position: 'relative' }}>
            {data.queue !== null && !data.sessionEnd && (
                <div style={{ position: 'absolute', top: '5px', right: '5px' }} ref={moreButtonRef}>
                    <IconButton onClick={handleClick}>
                        <MoreIcon />
                    </IconButton>
                    <PopupMenu
                        open={open}
                        bottom="-60px"
                        right="10px"
                        options={[
                            {
                                text: 'Skip this queue',
                                onClick: onSkip,
                            },
                        ]}
                        onClose={handleClose}
                    />
                </div>
            )}

            <StyledCardContent
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    rowGap: '10px',
                    height: '100%',
                }}
            >
                <div>
                    <Avatar
                        src={!data.patient?.user ? null : data.patient?.user?.avatar?.url}
                        alt={!data.patient?.user ? '' : data.patient?.user?.fullname}
                        $avatarSize="90px"
                        $borderRadius="50%"
                    />
                </div>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        rowGap: '3px',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            borderRadius: '4px',
                            border: `1px solid ${theme.palette.common.black}`,
                            backgroundColor: theme.palette.common.black,
                            overflow: 'hidden',
                        }}
                    >
                        {data.queue !== null && (
                            <div style={{ padding: '0 5px' }}>
                                <Text $color="white" $fontSize="10px">
                                    FR{data.queue}
                                </Text>
                            </div>
                        )}
                        <div
                            style={{
                                backgroundColor: theme.palette.common.white,
                                padding: '0 5px',
                            }}
                        >
                            <Text $color="gray1" $fontSize="10px">
                                {!_.isEmpty(data) &&
                                    `${dayjs()
                                        .set({
                                            hour: data.startTime.split(':')[0],
                                            minute: data.startTime.split(':')[1],
                                        })
                                        .format('HH:mm')} - ${dayjs()
                                        .set({
                                            hour: data.endTime.split(':')[0],
                                            minute: data.endTime.split(':')[1],
                                        })
                                        .format('HH:mm')}`}
                            </Text>
                        </div>
                    </div>
                    <Text $ellipsis $lineClamp={2} $fontSize="14px">
                        {!data.patient?.user ? 'Inactive' : data.patient?.user?.fullname}
                    </Text>
                    <div>
                        {data.queue !== null && !data.sessionEnd && (
                            <Text $color="green" $fontSize="10px">
                                Ready
                            </Text>
                        )}
                        {(data.sessionEnd || data.cancelled) && (
                            <Text $color="red" $fontSize="10px">
                                {data.sessionEnd ? 'Ended' : 'Cancelled'}
                            </Text>
                        )}
                    </div>
                </div>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        columnGap: '30px',
                    }}
                >
                    <FilledIconButton
                        $backgroundColor="blue2"
                        $height="43px"
                        $width="43px"
                        style={{ padding: 0, margin: 0 }}
                        onClick={onMessage}
                        disabled={!data.patient?.user}
                    >
                        <MessagesIcon />
                    </FilledIconButton>
                    <Tooltip
                        title={
                            <div style={{ padding: '5px 12px' }}>
                                <Text $fontSize="16px" $color="gray4">
                                    Send notification
                                </Text>
                            </div>
                        }
                        arrow
                        placement="top"
                    >
                        <div>
                            <FilledIconButton
                                $icon
                                $backgroundColor="purple"
                                $height="43px"
                                $width="43px"
                                onClick={onNotification}
                                disabled={!data.patient?.user || data.sessionEnd || disabled}
                            >
                                <NoticeIcon />
                            </FilledIconButton>
                        </div>
                    </Tooltip>
                    <FilledIconButton
                        $backgroundColor="green"
                        $disabledBackgroundColor="green6"
                        $height="43px"
                        $width="43px"
                        style={{ padding: 0, margin: 0 }}
                        onClick={onCall}
                        disabled={
                            data.queue === null ||
                            !data.patient?.user ||
                            data.sessionEnd ||
                            disabled
                        }
                        loading={loading}
                    >
                        <PhoneIcon />
                    </FilledIconButton>
                </div>
            </StyledCardContent>
        </StyledCard>
    );
};

PatientReadyCard.propTypes = {
    data: PropTypes.object.isRequired,
    onSkip: PropTypes.func,
    onMessage: PropTypes.func,
    onCall: PropTypes.func,
    onNotification: PropTypes.func,
    disabled: PropTypes.bool,
};
